import { useId, useState } from 'react';

import type { Business } from '../../types';
import { formatDistance, googleSearchHref } from '../../lib/format';
import { IconChevron } from './Icons';

interface TreatedFolderProps {
  businesses: readonly Business[];
  onUntreat: (business: Business) => void;
  pendingPlaceId: string | null;
}

/**
 * Le dossier « Déjà traités », replié par défaut.
 *
 * Rejoint depuis le prompt post-appel (« Refusé ») : l'établissement sort de
 * la liste principale sans être perdu, exactement comme le dossier « À
 * traiter » le fait pour ceux qui ont déjà un site. Pas de verdict ni
 * d'analyse ici — juste la mémoire qu'on est déjà passé, et le moyen de la
 * défaire.
 */
export function TreatedFolder(props: TreatedFolderProps) {
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  const { businesses } = props;

  if (businesses.length === 0) return null;

  return (
    <section className="folder">
      <button
        type="button"
        className="folder-head"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="badge badge-opp-1">{businesses.length}</span>
        <span className="folder-title">
          Déjà traités
          <span className="folder-note">
            {businesses.length > 1 ? 'ont été écartés' : 'a été écarté'} — appel refusé
          </span>
        </span>
        <IconChevron className="folder-chevron" size={16} />
      </button>

      {open ? (
        <ul className="rows" id={bodyId}>
          {businesses.map((business) => {
            const pending = props.pendingPlaceId === business.placeId;

            return (
              <li key={business.placeId} className="card-flat treat">
                <div className="treat-head">
                  <div style={{ minWidth: 0 }}>
                    <a
                      className="row-name row-name-link"
                      href={googleSearchHref(business.name, business.address)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Rechercher sur Google"
                    >
                      {business.name}
                    </a>
                    <p className="row-meta" style={{ margin: '3px 0 0' }}>
                      {business.category !== null ? <span>{business.category}</span> : null}
                      <span>{formatDistance(business.distanceMeters)}</span>
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => props.onUntreat(business)}
                    disabled={pending}
                  >
                    Oublier
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
}
